import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Pencil, Trash2, ArrowLeft, Phone, Mail, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, useApi, errMsg, HTTP_CONFLICT } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Loading, Empty, AvailBadge, Avatar, InfoRow, ConfirmDialog, Field, Badge } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { WorkerForm } from "../components/Forms";
import DocumentsPanel from "../components/DocumentsPanel";
import EquipmentPanel from "../components/EquipmentPanel";
import WorkerAssignments from "../components/WorkerAssignments";
import { WorkerDayPanel } from "../components/WorkerDay";
import ConflictDialog from "../components/ConflictDialog";
import { eur2, fmtDate, todayISO } from "../lib/format";

function ExpiryTable({ title, items }) {
  const today = todayISO();
  return (
    <div className="mb-5"><div className="eyebrow mb-2">{title}</div>
      {!items?.length ? <div className="text-sm text-muted-foreground">Nessuno</div> : items.map((it) => (
        <div key={`${it.name}-${it.expiry}`} className="flex justify-between py-2 border-b border-border/60 text-sm"><span>{it.name}</span>
          <span className={it.expiry && it.expiry < today ? "text-red-400 num" : "num"}>{it.expiry ? `Scad. ${fmtDate(it.expiry)}` : "—"}</span></div>
      ))}
    </div>
  );
}

function Absences({ worker, isAdmin, reload }) {
  const [f, setF] = useState({ start_date: todayISO(), end_date: todayISO(), reason: "" });
  const [conflict, setConflict] = useState(null);
  const add = async () => {
    try {
      await api.post(`/workers/${worker.id}/absences`, f);
      toast.success("✓ Assenza registrata");
      reload();
    } catch (e) {
      if (e.response?.status === HTTP_CONFLICT) setConflict([{ name: worker.full_name, conflicts: e.response.data.detail.conflicts }]);
      else toast.error(errMsg(e));
    }
  };
  const del = async (id) => {
    await api.delete(`/workers/absences/${id}`);
    toast.success("✓ Assenza eliminata");
    reload();
  };
  return (
    <div>
      {isAdmin && (
        <div className="grid sm:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end mb-4 p-3 rounded-lg border border-dashed border-border">
          <Field label="Dal"><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="absence-start-input" /></Field>
          <Field label="Al"><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="absence-end-input" /></Field>
          <Field label="Motivo"><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Permesso, visita…" /></Field>
          <Button onClick={add} data-testid="absence-add-btn"><Plus className="h-4 w-4 mr-1" />Registra</Button>
        </div>
      )}
      {!worker.absences.length ? <Empty title="Nessuna assenza registrata" /> : worker.absences.map((a) => (
        <div key={a.id} className="flex items-center justify-between py-2.5 border-b border-border/60 text-sm">
          <span><Badge className="bg-orange-950 border-orange-500 text-orange-200 mr-2">🟠 Assente</Badge>{fmtDate(a.start_date)} → {fmtDate(a.end_date)} <span className="text-muted-foreground">{a.reason}</span></span>
          {isAdmin && <Button size="icon" variant="ghost" onClick={() => del(a.id)}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ))}
      <ConflictDialog open={!!conflict} onOpenChange={() => setConflict(null)} items={conflict || []} />
    </div>
  );
}

function WorkerHeader({ w, isAdmin, onEdit, onDelete }) {
  return (
    <div className="surface p-5 sm:p-6 mb-6 flex flex-col md:flex-row md:items-center gap-5 fade-up">
      <Avatar name={w.full_name} photo={w.photo} size="h-20 w-20" className="text-xl" />
      <div className="flex-1">
        <div className="eyebrow">{w.qualification} {w.ccnl_level && `· ${w.ccnl_level}`}</div>
        <h1 className="text-2xl sm:text-3xl font-bold" data-testid="worker-name">{w.full_name}</h1>
        <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
          {w.phone && <a href={`tel:${w.phone}`} className="flex items-center gap-1 hover:text-white"><Phone className="h-4 w-4" />{w.phone}</a>}
          {w.email && <span className="flex items-center gap-1"><Mail className="h-4 w-4" />{w.email}</span>}
        </div>
      </div>
      <div className="rounded-xl border border-border p-4 min-w-[220px]" data-testid="worker-availability-box">
        <div className="eyebrow mb-2">Stato disponibilità</div>
        <AvailBadge status={w.availability.status} testid="worker-availability-badge" />
        {w.availability.site_name && <div className="text-xs text-muted-foreground mt-2">Cantiere: {w.availability.site_name}</div>}
      </div>
      {isAdmin && (
        <div className="flex gap-2">
          <Button variant="outline" onClick={onEdit} data-testid="worker-edit-btn"><Pencil className="h-4 w-4 mr-2" />Modifica</Button>
          <Button variant="outline" className="text-red-400 border-red-900" onClick={onDelete} data-testid="worker-delete-btn"><Trash2 className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}

const WorkerInfoTab = ({ w }) => (
  <div className="grid lg:grid-cols-2 gap-6">
    <div className="surface p-5">
      <InfoRow label="Codice fiscale" value={w.fiscal_code} /><InfoRow label="Data di nascita" value={fmtDate(w.birth_date)} />
      <InfoRow label="Luogo di nascita" value={w.birth_place} /><InfoRow label="Nazionalità" value={w.nationality} />
      <InfoRow label="Indirizzo" value={w.address} /><InfoRow label="IBAN" value={w.iban} /><InfoRow label="Account app" value={w.user_account} />
    </div>
    <div className="surface p-5">
      <InfoRow label="Data assunzione" value={fmtDate(w.hire_date)} /><InfoRow label="Costo orario lordo" value={w.hourly_gross != null ? eur2(w.hourly_gross) : null} />
      <InfoRow label="Costo aziendale orario" value={eur2(w.hourly_company_cost)} /><InfoRow label="Taglia DPI" value={w.dpi_size} />
      <InfoRow label="Note disciplinari" value={w.disciplinary_notes} /><InfoRow label="Note" value={w.notes} />
    </div>
  </div>
);

function WorkerQualTab({ w }) {
  const { medical_fitness: fitness, medical_expiry: expiry } = w;
  const medical = useMemo(() => [{ name: fitness || "Idoneità", expiry }], [fitness, expiry]);
  return (
    <div className="surface p-5 grid md:grid-cols-3 gap-6">
      <ExpiryTable title="Patentini" items={w.licenses} />
      <ExpiryTable title="Attestati" items={w.certificates} />
      <ExpiryTable title="Idoneità medica" items={medical} />
    </div>
  );
}

export default function WorkerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const { data: w, loading, reload } = useApi(`/workers/${id}`);
  const [edit, setEdit] = useState(false);
  const [del, setDel] = useState(false);

  if (loading && !w) return <Loading />;
  if (!w) return <Empty title="Operaio non trovato" />;
  const remove = async () => {
    try {
      await api.delete(`/workers/${w.id}`);
      toast.success("✓ Operaio eliminato");
      nav("/operai");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <button onClick={() => nav(-1)} className="text-sm text-muted-foreground hover:text-white flex items-center gap-1 mb-4" data-testid="back-btn"><ArrowLeft className="h-4 w-4" />Indietro</button>
      <WorkerHeader w={w} isAdmin={isAdmin} onEdit={() => setEdit(true)} onDelete={() => setDel(true)} />

      <Tabs defaultValue="anagrafica">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="anagrafica" data-testid="tab-anagrafica">Anagrafica</TabsTrigger>
          <TabsTrigger value="qualifiche" data-testid="tab-qualifiche">Qualifiche e scadenze</TabsTrigger>
          <TabsTrigger value="assegnazioni" data-testid="tab-assegnazioni">Assegnazioni</TabsTrigger>
          <TabsTrigger value="giornata" data-testid="tab-giornata">Giornata</TabsTrigger>
          <TabsTrigger value="assenze" data-testid="tab-assenze">Assenze</TabsTrigger>
          <TabsTrigger value="dotazioni" data-testid="tab-dotazioni">Attrezzi e dotazioni</TabsTrigger>
          {isAdmin && <TabsTrigger value="documenti" data-testid="tab-documenti">Documenti</TabsTrigger>}
        </TabsList>
        <TabsContent value="anagrafica"><WorkerInfoTab w={w} /></TabsContent>
        <TabsContent value="qualifiche"><WorkerQualTab w={w} /></TabsContent>
        <TabsContent value="assegnazioni">
          <div className="surface p-5"><WorkerAssignments worker={w} reload={reload} /></div>
        </TabsContent>
        <TabsContent value="giornata"><WorkerDayPanel workerId={w.id} /></TabsContent>
        <TabsContent value="assenze"><div className="surface p-5"><Absences worker={w} isAdmin={isAdmin} reload={reload} /></div></TabsContent>
        <TabsContent value="dotazioni"><div className="surface p-5"><EquipmentPanel workerId={w.id} items={w.equipment} canEdit reload={reload} /></div></TabsContent>
        {isAdmin && <TabsContent value="documenti"><div className="surface p-5"><DocumentsPanel entityType="worker" entityId={w.id} canEdit /></div></TabsContent>}
      </Tabs>
      <WorkerForm open={edit} onOpenChange={setEdit} worker={w} onSaved={reload} />
      <ConfirmDialog open={del} onOpenChange={setDel} title={`Eliminare ${w.full_name}?`} description="Verranno eliminate anche tutte le assegnazioni e assenze dell'operaio." onConfirm={remove} />
    </div>
  );
}
